"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import theme from "@/theme/theme";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: "dark" | "light" | "system";
};

type ThemeProviderState = {
  theme: "dark" | "light";
  setTheme: (theme: "dark" | "light" | "system") => void;
  primaryColor: string;
};

const initialState: ThemeProviderState = {
  theme: "dark",
  setTheme: () => null,
  primaryColor: theme.colors.primary.hex,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({ children, defaultTheme = "dark" }: ThemeProviderProps) {
  const [themeMode, setThemeMode] = useState<"dark" | "light">(
    defaultTheme === "system" ? "dark" : defaultTheme
  );

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(themeMode);
  }, [themeMode]);

  const value = {
    theme: themeMode,
    setTheme: (theme: "dark" | "light" | "system") => {
      setThemeMode(theme === "system" ? "dark" : theme);
    },
    primaryColor: theme.colors.primary.hex,
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
